import { PDFJS_ASSETS, pdfjsLib } from "./pdf-library";
import {
  EventBus,
  PDFLinkService,
  PDFViewer,
} from "pdfjs-dist/web/pdf_viewer.mjs";
import type { PDFDocumentProxy, RenderTask } from "pdfjs-dist";

// Side by side splits the canvas and moves both sheets together; overlay
// draws the other sheet over this one, red for this sheet and blue for the
// other, so linework on both reads dark and a change shows in colour.
export type PdfCompareMode = "SIDE_BY_SIDE" | "OVERLAY";

// pdf.js's RenderingStates.FINISHED, which pdf_viewer.mjs does not export
const PAGE_RENDERED = 3;

// an overlaid sheet is drawn no sharper than pdf.js draws the page under it
const OVERLAY_MAX_PIXELS = 2 ** 25;

/**
 * Compares the open PDF with a second one: another version of the same file,
 * or any other PDF in the project. The second PDF never replaces the first;
 * it is only drawn beside it or over it, page for page, with an offset when
 * the sheet to compare against sits on a different page.
 */
export class PdfComparer {
  private viewer: PDFViewer;
  private eventBus: EventBus;
  private container: HTMLDivElement;
  private compareContainer: HTMLDivElement;
  // the second viewer, made the first time side by side is used
  private compareViewer: PDFViewer | null = null;
  private compareEventBus: EventBus | null = null;
  private compareDocument: PDFDocumentProxy | null = null;
  private loadingTask: any = null;
  // a newer Open, or a Close, makes an older load that lands late stale
  private openCount = 0;
  private mode: PdfCompareMode | null = null;
  private pageOffset = 0;
  private overlayTasks = new Map<number, RenderTask>();
  private panStart: {
    x: number;
    y: number;
    scrollLeft: number;
    scrollTop: number;
  } | null = null;

  constructor(
    viewer: PDFViewer,
    eventBus: EventBus,
    container: HTMLDivElement,
    compareContainer: HTMLDivElement,
  ) {
    this.viewer = viewer;
    this.eventBus = eventBus;
    this.container = container;
    this.compareContainer = compareContainer;

    this.eventBus.on("pagerendered", this.HandlePageRendered);
    this.eventBus.on("scalechanging", this.SyncScroll);
    this.eventBus.on("rotationchanging", this.SyncScroll);
    this.container.addEventListener("scroll", this.SyncScroll, {
      passive: true,
    });
    // the compared sheet has no scrollbars of its own; scrolling, zooming and
    // dragging it move this sheet, which it follows
    this.compareContainer.addEventListener("wheel", this.HandleCompareWheel, {
      passive: false,
    });
    this.compareContainer.addEventListener("mousedown", this.HandlePanStart);
  }

  get active(): boolean {
    return this.mode !== null && this.compareDocument !== null;
  }

  /**
   * Loads the PDF to compare against and returns its page count. Whatever is
   * being compared already stays on screen until it has arrived.
   */
  async Open(url: string): Promise<number> {
    const openCount = ++this.openCount;
    const documentOptions = {
      url,
      cMapUrl: `${PDFJS_ASSETS}/cmaps/`,
      cMapPacked: true,
      standardFontDataUrl: `${PDFJS_ASSETS}/standard_fonts/`,
    };

    let loadingTask = pdfjsLib.getDocument(documentOptions);
    let compareDocument: PDFDocumentProxy;
    try {
      compareDocument = await loadingTask.promise;
    } catch (error: any) {
      if (
        error?.name === "PasswordException" ||
        error?.name === "InvalidPDFException"
      ) {
        throw error;
      }
      // the same fallback as the open PDF: the whole file in one request
      loadingTask = pdfjsLib.getDocument({
        ...documentOptions,
        disableRange: true,
        disableStream: true,
      });
      compareDocument = await loadingTask.promise;
    }

    if (openCount !== this.openCount) {
      loadingTask.destroy();
      throw new Error("Superseded");
    }

    this.ClearOverlay();
    const previousTask = this.loadingTask;
    this.loadingTask = loadingTask;
    this.compareDocument = compareDocument;
    // the second viewer takes the new PDF once it is shown; hidden, pdf.js
    // cannot place its pages
    this.compareViewer?.setDocument(null as any);
    previousTask?.destroy();
    this.Apply();

    return compareDocument.numPages;
  }

  // Stops comparing and lets the second PDF go.
  Close() {
    this.openCount++;
    this.ClearOverlay();
    this.ShowSideBySide(false);
    this.compareViewer?.setDocument(null as any);
    this.loadingTask?.destroy();
    this.loadingTask = null;
    this.compareDocument = null;
  }

  // null hides the comparison and keeps the second PDF for when it is back
  SetMode(mode: PdfCompareMode | null) {
    if (mode === this.mode) return;

    this.mode = mode;
    this.Apply();
  }

  // page n of this PDF is compared with page n + pageOffset of the other
  SetPageOffset(pageOffset: number) {
    if (pageOffset === this.pageOffset) return;

    this.pageOffset = pageOffset;
    this.Apply();
  }

  /**
   * Balances the overlay: 0 shows only this sheet, 1 only the other, and
   * anything in between fades one of them, with both full at 0.5.
   */
  SetFade(fade: number) {
    this.container.style.setProperty(
      "--pdf-compare-current",
      String(Math.min(1, 2 * (1 - fade))),
    );
    this.container.style.setProperty(
      "--pdf-compare-other",
      String(Math.min(1, 2 * fade)),
    );
  }

  Dispose() {
    this.Close();
    this.eventBus.off("pagerendered", this.HandlePageRendered);
    this.eventBus.off("scalechanging", this.SyncScroll);
    this.eventBus.off("rotationchanging", this.SyncScroll);
    this.container.removeEventListener("scroll", this.SyncScroll);
    this.compareContainer.removeEventListener("wheel", this.HandleCompareWheel);
    this.compareContainer.removeEventListener("mousedown", this.HandlePanStart);
    this.HandlePanEnd();
    this.compareEventBus?.off("pagesinit", this.SyncScroll);
    this.compareEventBus?.off("scalechanging", this.SyncScroll);
    this.compareViewer = null;
    this.compareEventBus = null;
  }

  // Shows the comparison the mode, the offset and the PDF now ask for.
  private Apply() {
    const overlay = this.active && this.mode === "OVERLAY";
    this.container.classList.toggle("pdfCompareOverlay", overlay);
    this.ClearOverlay();
    if (overlay) {
      for (let index = 0; index < this.viewer.pagesCount; index++) {
        const pageView = this.viewer.getPageView(index) as any;
        if (pageView?.renderingState === PAGE_RENDERED) {
          this.DrawOverlayPage(index + 1);
        }
      }
    }

    this.ShowSideBySide(this.active && this.mode === "SIDE_BY_SIDE");
    this.SyncScroll();
  }

  // ---- side by side

  private ShowSideBySide(show: boolean) {
    // the canvas splits first, so the second viewer is made at its own size
    // and this one refits to its half
    this.container.parentElement?.classList.toggle(
      "pdfCompareSideBySide",
      show,
    );
    if (!show || !this.compareDocument) return;

    if (!this.compareViewer) {
      this.compareEventBus = new EventBus();
      const linkService = new PDFLinkService({
        eventBus: this.compareEventBus,
      });
      this.compareViewer = new PDFViewer({
        container: this.compareContainer,
        eventBus: this.compareEventBus,
        linkService,
        // only looked at: no text to select and nothing to mark up
        textLayerMode: 0,
        annotationMode: pdfjsLib.AnnotationMode.ENABLE,
        annotationEditorMode: pdfjsLib.AnnotationEditorType.DISABLE,
      });
      linkService.setViewer(this.compareViewer);
      this.compareEventBus.on("pagesinit", this.SyncScroll);
      // pdf.js keeps its own place when it zooms, which this one overrides
      this.compareEventBus.on("scalechanging", this.SyncScroll);
    }
    if (this.compareViewer.pdfDocument !== this.compareDocument) {
      this.compareViewer.setDocument(this.compareDocument);
    } else {
      // hidden, it drew nothing; shown again, it draws the pages in view
      this.compareViewer.update();
    }
  }

  /**
   * Puts the compared sheet at the same zoom and turn as this one, and at the
   * same spot of its page: the page in the middle of the view, and how far
   * into it the view has scrolled.
   */
  private SyncScroll = () => {
    const compareViewer = this.compareViewer;
    if (
      !compareViewer?.pagesCount ||
      !this.viewer.pagesCount ||
      this.mode !== "SIDE_BY_SIDE"
    ) {
      return;
    }

    // each set moves the second viewer and comes back here, matched
    if (compareViewer.pagesRotation !== this.viewer.pagesRotation) {
      compareViewer.pagesRotation = this.viewer.pagesRotation;
    }
    if (compareViewer.currentScale !== this.viewer.currentScale) {
      compareViewer.currentScale = this.viewer.currentScale;
    }

    const middle = this.container.scrollTop + this.container.clientHeight / 2;
    let pageIndex = 0;
    for (let index = 1; index < this.viewer.pagesCount; index++) {
      if ((this.viewer.getPageView(index) as any).div.offsetTop > middle) {
        break;
      }
      pageIndex = index;
    }
    const pageDiv: HTMLDivElement = (this.viewer.getPageView(pageIndex) as any)
      .div;
    const compareIndex = Math.max(
      0,
      Math.min(compareViewer.pagesCount - 1, pageIndex + this.pageOffset),
    );
    const compareDiv: HTMLDivElement = (
      compareViewer.getPageView(compareIndex) as any
    ).div;

    this.compareContainer.scrollTop =
      compareDiv.offsetTop + (this.container.scrollTop - pageDiv.offsetTop);
    this.compareContainer.scrollLeft =
      compareDiv.offsetLeft + (this.container.scrollLeft - pageDiv.offsetLeft);
  };

  private HandleCompareWheel = (event: WheelEvent) => {
    event.preventDefault();
    if (event.ctrlKey || event.metaKey) {
      if (event.deltaY < 0) {
        this.viewer.increaseScale();
      } else if (event.deltaY > 0) {
        this.viewer.decreaseScale();
      }
    } else {
      this.container.scrollBy(event.deltaX, event.deltaY);
    }
  };

  private HandlePanStart = (event: MouseEvent) => {
    if (event.button !== 0) return;

    event.preventDefault();
    this.panStart = {
      x: event.clientX,
      y: event.clientY,
      scrollLeft: this.container.scrollLeft,
      scrollTop: this.container.scrollTop,
    };
    this.compareContainer.classList.add("pdfPanning");
    window.addEventListener("mousemove", this.HandlePanMove);
    window.addEventListener("mouseup", this.HandlePanEnd);
  };

  private HandlePanMove = (event: MouseEvent) => {
    if (!this.panStart) return;

    this.container.scrollLeft =
      this.panStart.scrollLeft - (event.clientX - this.panStart.x);
    this.container.scrollTop =
      this.panStart.scrollTop - (event.clientY - this.panStart.y);
  };

  private HandlePanEnd = () => {
    this.panStart = null;
    this.compareContainer.classList.remove("pdfPanning");
    window.removeEventListener("mousemove", this.HandlePanMove);
    window.removeEventListener("mouseup", this.HandlePanEnd);
  };

  // ---- overlay

  // pdf.js draws a page again at each zoom, so its overlay is drawn again too
  private HandlePageRendered = ({ pageNumber }: { pageNumber: number }) => {
    if (this.active && this.mode === "OVERLAY") {
      this.DrawOverlayPage(pageNumber);
    }
  };

  /**
   * Draws the other PDF's page at the same scale and turn as this page, in
   * its own canvas over this page's. The stylesheet colours the two and
   * multiplies one over the other. The old overlay stays until the new one is
   * ready, so zooming does not flash.
   */
  private async DrawOverlayPage(pageNumber: number) {
    const compareDocument = this.compareDocument;
    const pageView = this.viewer.getPageView(pageNumber - 1) as any;
    const canvasWrapper: HTMLDivElement | null =
      pageView?.div.querySelector(".canvasWrapper");
    if (!compareDocument || !canvasWrapper) return;

    this.overlayTasks.get(pageNumber)?.cancel();
    this.overlayTasks.delete(pageNumber);

    const comparePageNumber = pageNumber + this.pageOffset;
    if (comparePageNumber < 1 || comparePageNumber > compareDocument.numPages) {
      canvasWrapper.querySelector(".pdfCompareCanvas")?.remove();
      return;
    }

    try {
      const comparePage = await compareDocument.getPage(comparePageNumber);
      if (compareDocument !== this.compareDocument) return;

      const pageViewport = pageView.viewport;
      const viewport = comparePage.getViewport({
        scale: pageViewport.scale,
        rotation: (comparePage.rotate + this.viewer.pagesRotation) % 360,
      });
      const outputScale = Math.min(
        window.devicePixelRatio || 1,
        Math.sqrt(OVERLAY_MAX_PIXELS / (viewport.width * viewport.height)),
      );

      const canvas = document.createElement("canvas");
      canvas.className = "pdfCompareCanvas";
      canvas.width = Math.floor(viewport.width * outputScale);
      canvas.height = Math.floor(viewport.height * outputScale);
      // sized against this page, so it follows pdf.js's zoom until redrawn
      canvas.style.width = `${(viewport.width / pageViewport.width) * 100}%`;
      canvas.style.height = `${(viewport.height / pageViewport.height) * 100}%`;

      const renderTask = comparePage.render({
        canvasContext: canvas.getContext("2d")!,
        viewport,
        transform:
          outputScale !== 1
            ? [outputScale, 0, 0, outputScale, 0, 0]
            : undefined,
        annotationMode: pdfjsLib.AnnotationMode.ENABLE,
      });
      this.overlayTasks.set(pageNumber, renderTask);
      await renderTask.promise;
      this.overlayTasks.delete(pageNumber);

      if (compareDocument !== this.compareDocument || !this.active) return;
      canvasWrapper.querySelector(".pdfCompareCanvas")?.remove();
      canvasWrapper.appendChild(canvas);
    } catch (error: any) {
      if (error?.name !== "RenderingCancelledException") {
        console.error("Error drawing the compared page:", error);
      }
    }
  }

  private ClearOverlay() {
    for (const renderTask of this.overlayTasks.values()) {
      renderTask.cancel();
    }
    this.overlayTasks.clear();
    for (const canvas of this.container.querySelectorAll(".pdfCompareCanvas")) {
      canvas.remove();
    }
  }
}
